import type { SsnCountry, SsnError } from '@osteopro/shared';
import type { TFunction } from 'i18next';

/**
 * Phrases des refus d'un numéro de sécurité sociale.
 *
 * `validateSsn` rend des **codes** : c'est ici, et ici seulement, qu'ils deviennent des phrases —
 * pour le champ du formulaire comme pour le `422 ssn_invalid` du serveur. Deux codes ont une
 * phrase par pays, parce que la V1 disait « Le numéro CNS doit contenir exactement 13 chiffres »
 * et non une généralité : on garde ses mots.
 *
 * La table est un `Record` sur le type partagé, pas une liste : un code ajouté un jour au contrat
 * sans phrase ici casse la compilation au lieu d'afficher une clé brute à l'écran.
 */
const SSN_ERRORS: Readonly<Record<SsnError, true>> = {
  country_required: true,
  length: true,
  digits: true,
  sex_digit: true,
  month: true,
  day: true,
  year: true,
  date: true,
  key: true,
};

/** Les codes dont la phrase dépend du pays : la longueur et les caractères admis. */
const PER_COUNTRY: ReadonlySet<SsnError> = new Set<SsnError>(['length', 'digits']);

export function isSsnError(value: unknown): value is SsnError {
  return typeof value === 'string' && Object.hasOwn(SSN_ERRORS, value);
}

/**
 * La phrase d'un code, dans la langue de l'écran. Sans pays — un refus du serveur n'en dit pas —
 * ou pour « autre », la phrase générale.
 */
export function ssnErrorMessage(code: SsnError, country: SsnCountry | null, t: TFunction): string {
  const specific = country !== null && country !== 'OTHER' && PER_COUNTRY.has(code);
  const key = specific ? `patients:ssn.errors.${code}_${country}` : `patients:ssn.errors.${code}`;
  return t(key, { year: new Date().getFullYear() });
}
